/**
 * Pool Reports Net Allocation Page
 *
 * Displays net allocation data in a table format matching Angular implementation.
 * Uses CSS to hide "Balance Undistributed" columns based on toggle state.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { useNetAllocations } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { Button, Toggle } from '@/components/ui';
import { PoolReportTableCard } from '@/components/poolReports/PoolReportTableCard';
import { formatNumber } from '@/utils/formatters';
import { CSVService } from '@/utils/csvExport';
import type { NetAllocation } from '@/types/poolReportsExtended.types';
import {
  poolReportOwnerLabelClass,
  poolReportOwnerLinkClass,
  poolReportSelectedCellClass,
} from '@/components/poolReports/poolReportTableHighlight';
import {
  POOL_REPORT_OWNER_COL_HEADER,
  poolReportTheadStickyClass,
  poolReportThEmphasisClass,
  poolReportTdPadClass,
} from '@/components/poolReports/poolReportTableChrome';

export function PoolReportsNetAllocationPage() {
  const navigate = useNavigate();
  const { selectedReport, reportType, selectedPoolId } = usePoolReportsStore();
  const fileId = selectedReport?.fileId;

  const [showBalance, setShowBalance] = useState(false);
  const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null);

  const { data: netAllocations = [], isLoading } = useNetAllocations(fileId || 0, !!fileId);

  // Process data: suppress columns with all values < 10
  const processedData = netAllocations.length > 0 ? suppressLowValueColumns(netAllocations) : [];

  // Get columns from first row (exclude ownerId from display)
  const columns =
    processedData.length > 0
      ? Object.keys(processedData[0]).filter((key) => {
          const lowerKey = key.toLowerCase();
          return lowerKey !== 'ownerid' && lowerKey !== 'withlink';
        })
      : [];

  // Suppress columns where all owner rows have values < 10
  // Matches Angular: suppressColumnLessThan10Vaue()
  // Checks Balance Undistributed year columns (but NOT "Prior Years")
  // Deletes them from data if all owner values are < 10
  function suppressLowValueColumns(data: NetAllocation[]): NetAllocation[] {
    const columnsToCheck: string[] = [];
    Object.keys(data[0]).forEach((key) => {
      // Check Balance Undistributed year columns (but not "Prior Years")
      if (key.startsWith('Balance Undistributed') && !key.includes('Prior Years')) {
        columnsToCheck.push(key);
      }
    });

    const columnsToSuppress = columnsToCheck.filter((column) => {
      const hasSignificantValue = data
        .filter((row) => row.OwnerID || row.ownerId) // Exclude total row - check both cases
        .some((row) => {
          const value = row[column];
          return typeof value === 'number' && Math.abs(value) >= 10;
        });

      return !hasSignificantValue;
    });

    return data.map((row) => {
      const newRow = { ...row };
      columnsToSuppress.forEach((column) => {
        delete newRow[column];
      });
      return newRow;
    });
  }

  // Convert column name to CSS classes - exactly like Angular's lowercase pipe
  // Angular: [ngClass]="item.key+'' | lowercase"
  // This converts "Balance Undistributed Prior Years" to "balance undistributed prior years"
  // which becomes multiple classes: .balance .undistributed .prior .years
  function getColumnClass(columnName: string): string {
    return columnName.toLowerCase();
  }

  // Navigate to owner details
  function handleOwnerClick(allocation: NetAllocation) {
    const ownerId = allocation.ownerId || allocation.OwnerID;
    if (!ownerId) return; // Don't navigate for total row

    const ownerName =
      allocation.ownerName ||
      // Angular data sometimes uses "Owner Name"
      (allocation as Record<string, unknown>)['Owner Name'] ||
      '';

    navigate(
      `/reports/${reportType}/net-allocation/owner/${ownerId}?poolId=${selectedPoolId}&fileId=${fileId}&ownerName=${encodeURIComponent(String(ownerName))}`
    );
  }

  // Export to CSV
  function handleExport() {
    if (!selectedReport || processedData.length === 0) return;

    let cols = columns;

    // Filter columns based on showBalance
    if (!showBalance) {
      cols = columns.filter((col) => {
        const lower = col.toLowerCase();
        if (lower.includes('balance undistributed prior years')) return true;
        if (lower.includes('balance undistributed')) return false;
        return true;
      });
    }

    const rows = processedData.map((item) =>
      cols.map((col, index) => {
        const value = item[col];
        if (value === null || value === undefined) return '';
        if (index > 0) return formatNumber(value as number, 0);
        return String(value);
      })
    );

    CSVService.export(rows, `${selectedReport.description}-net-allocation-report`, cols);
  }

  // Render loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <PoolReportTableCard
          eyebrow="Net allocation"
          title="Owner summary"
          description="Loading allocation columns…"
          endAdornment={
            <div className="h-9 w-full max-w-[11rem] animate-pulse rounded-full bg-gray-200 dark:bg-dark-700 sm:w-40" />
          }
        >
          <div className="report-table-shell">
            <div className="min-w-[1200px] text-sm tabular-nums">
              <div className="grid grid-cols-12 bg-gray-100/90 dark:bg-dark-900/70">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="border-b border-gray-200 p-2.5 dark:border-dark-700 sm:p-3"
                  >
                    <div className="ml-auto h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
                  </div>
                ))}
              </div>
              {[...Array(7)].map((_, i) => (
                <div key={i} className="grid grid-cols-12">
                  {[...Array(12)].map((_, j) => (
                    <div
                      key={j}
                      className="border-b border-gray-200 bg-white p-2.5 dark:border-dark-700 dark:bg-dark-900 sm:p-3"
                    >
                      <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </PoolReportTableCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* CSS for hiding Balance Undistributed columns (except Prior Years) - matches Angular */}
      <style>{`
        /* Hide "Balance Undistributed" columns that don't have "prior" class */
        th.balance.undistributed:not(.prior),
        td.balance.undistributed:not(.prior) {
          display: none !important;
        }

        /* When toggle is on, show all Balance Undistributed columns */
        table.show-balance-details th.balance.undistributed,
        table.show-balance-details td.balance.undistributed {
          display: table-cell !important;
        }

        /* First column: sticky; header uses compact th typography (body cells keep readable size on mobile) */
        @media (min-width: 768px) {
          .net-allocation-table th:first-child,
          .net-allocation-table td:first-child {
            position: sticky;
            left: 0;
            min-width: 12rem;
            max-width: min(40vw, 24rem);
            border-right: solid 1px rgb(229 231 235);
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            vertical-align: top;
            box-shadow: 2px 0 6px rgba(15, 23, 42, 0.06);
            clip-path: inset(0px -10px 0px 0px);
            z-index: 9;
          }
          .net-allocation-table td:first-child {
            font-weight: bold;
            text-transform: uppercase;
          }
          .dark .net-allocation-table th:first-child,
          .dark .net-allocation-table td:first-child {
            border-right: solid 1px rgb(51 65 85);
            box-shadow: 2px 0 8px rgba(0, 0, 0, 0.35);
          }
        }

        @media (max-width: 767px) {
          .net-allocation-table tbody td:first-child {
            position: static !important;
            left: auto !important;
            min-width: 11rem;
            max-width: none;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            overflow: visible;
            vertical-align: top;
            font-size: 0.875rem;
            line-height: 1.4;
            padding: 0.5rem 0.65rem;
            border-right: 1px solid rgb(229 231 235);
            box-shadow: none !important;
            clip-path: none !important;
          }
          .net-allocation-table thead th:first-child {
            position: static !important;
            left: auto !important;
            min-width: 11rem;
            max-width: none;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            overflow: visible;
            vertical-align: top;
            border-right: 1px solid rgb(229 231 235);
            box-shadow: none !important;
            clip-path: none !important;
          }
          .dark .net-allocation-table thead th:first-child,
          .dark .net-allocation-table tbody td:first-child {
            border-right-color: rgb(51 65 85);
          }
        }
      `}</style>

      <PoolReportTableCard
        eyebrow="Net allocation"
        title="Owner summary"
        description="Net amounts by owner and period. Use the toggle to show balance undistributed columns (except prior years, always visible when present)."
        endAdornment={
          <>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200/80 bg-white/80 px-2.5 py-1.5 dark:border-dark-600 dark:bg-dark-900/40">
              <Toggle
                id="showBalance"
                checked={showBalance}
                onChange={setShowBalance}
                disabled={isLoading}
              />
              <label
                htmlFor="showBalance"
                className="text-xs font-medium text-gray-700 dark:text-gray-300 sm:text-sm"
              >
                Prior Years Balance
              </label>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              leftIcon={<Download className="h-4 w-4" />}
              disabled={isLoading || processedData.length === 0}
              className="w-full rounded-full px-4 shadow-sm sm:w-auto"
            >
              Export to Excel
            </Button>
          </>
        }
      >
        <div className="report-table-shell">
          <table
            className={`net-allocation-table report-table relative ${showBalance ? 'show-balance-details' : ''}`}
          >
            <thead className={poolReportTheadStickyClass}>
              <tr>
                {columns.map((column, colIndex) => {
                  const cssClass = getColumnClass(column);
                  const isOwnerColumn = colIndex === 0;
                  return (
                    <th
                      key={column}
                      className={clsx(
                        poolReportThEmphasisClass,
                        cssClass,
                        isOwnerColumn
                          ? 'bg-gray-100/95 dark:bg-dark-900/95 md:sticky md:left-0 md:z-30 md:min-w-[12rem] md:max-w-[min(40vw,24rem)] md:shadow-sm'
                          : 'whitespace-nowrap'
                      )}
                    >
                      {isOwnerColumn ? POOL_REPORT_OWNER_COL_HEADER : column}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {processedData.map((allocation, rowIndex) => {
                const isTotal = !allocation.ownerId && !allocation.OwnerID;
                const isFirstRow = rowIndex === 0;
                const rowKey = isTotal
                  ? '__total__'
                  : String(allocation.ownerId ?? allocation.OwnerID ?? `idx-${rowIndex}`);
                const isHighlighted = !isTotal && highlightedRowKey === rowKey;
                const hi = poolReportSelectedCellClass(isHighlighted);

                return (
                  <tr
                    key={rowIndex}
                    onClick={() => {
                      if (isTotal) return;
                      setHighlightedRowKey((prev) => (prev === rowKey ? null : rowKey));
                    }}
                    className={clsx(
                      isTotal
                        ? 'bg-gradient-to-r from-gray-100 to-gray-50/95 dark:from-dark-700/80 dark:to-dark-800/60'
                        : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-800'
                    )}
                  >
                    {columns.map((column, colIndex) => {
                      const value = allocation[column];
                      const isOwnerColumn = colIndex === 0;
                      const cssClass = getColumnClass(column);

                      const ownerStickyMd =
                        'md:sticky md:left-0 md:z-10 md:min-w-[12rem] md:max-w-[min(40vw,24rem)] md:shadow-sm md:align-top';

                      const cellClass = (() => {
                        const pad = `${poolReportTdPadClass} ` + (isFirstRow ? 'border-t-2 ' : '');
                        const num = `${pad}tabular-nums `;

                        if (isOwnerColumn) {
                          if (isTotal) {
                            return (
                              pad +
                              'border-gray-300 bg-gray-100 text-gray-900 dark:border-dark-600 dark:bg-dark-800 md:border-l-[3px] md:border-primary-600 dark:md:border-primary-500 ' +
                              ownerStickyMd +
                              ' ' +
                              cssClass +
                              ' ' +
                              hi
                            );
                          }
                          return (
                            pad +
                            'border-gray-200 bg-white text-gray-900 dark:border-dark-700 dark:bg-dark-900 md:bg-white dark:md:bg-dark-900 ' +
                            ownerStickyMd +
                            ' ' +
                            cssClass +
                            ' ' +
                            hi
                          );
                        }

                        if (isTotal) {
                          return (
                            num +
                            'border-gray-300 bg-gray-100/90 font-bold text-gray-900 dark:border-dark-600 dark:bg-dark-800/90 ' +
                            cssClass +
                            ' ' +
                            hi
                          );
                        }

                        return (
                          num +
                          'whitespace-nowrap border-gray-200 bg-white text-gray-900 dark:border-dark-700 dark:bg-dark-900 ' +
                          cssClass +
                          ' ' +
                          hi
                        );
                      })();

                      return (
                        <td key={column} className={cellClass}>
                          {isOwnerColumn && value ? (
                            isTotal ? (
                              <span className={poolReportOwnerLabelClass}>{value}</span>
                            ) : (
                              <div className="flex w-full min-w-0 flex-row items-center justify-end gap-2">
                                <button
                                  type="button"
                                  className={clsx(
                                    poolReportOwnerLabelClass,
                                    poolReportOwnerLinkClass,
                                    'min-w-0 flex-1 text-right'
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOwnerClick(allocation);
                                  }}
                                >
                                  {value}
                                </button>
                                <ExternalLink
                                  className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400"
                                  aria-hidden
                                />
                              </div>
                            )
                          ) : (
                            <span>
                              {typeof value === 'number' ? formatNumber(value, 0) : value || ''}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PoolReportTableCard>

      {/* No Data */}
      {!isLoading && processedData.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No net allocation data available</p>
        </div>
      )}
    </div>
  );
}
